import { Europafacts } from "@/services/api";
import { useCurrentLanguage, useLanguages } from "@/stores/language";
import { logger } from "@/utils/debug";
import type {
  DatasetMetadata,
  DatasetResult,
  Language,
} from "@/types/europafacts";
import { formatBytes } from "@/utils/format";
import {
  Button,
  Divider,
  Flex,
  Group,
  Image,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import type { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

export function DiscoverResultsDetailsPage() {
  const { datasetId } = useParams();
  const [metadata, setMetadata] = useState<DatasetMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [dataset, setDataset] = useState<DatasetResult[]>([]);
  const currentLanguage = useCurrentLanguage();
  const navigate = useNavigate();
  const languages = useLanguages();

  async function fetchData(datasetId: string) {
    setLoading(true);
    try {
      await fetchMetadata(datasetId);
      await fetchDataset(datasetId);
    } catch (error) {
      logger.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchMetadata(datasetId: string) {
    const data = await Europafacts.getMetadataForDataset(datasetId);
    logger.log(data);
    setMetadata(data);
  }

  async function fetchDataset(datasetId: string) {
    const data = await Europafacts.getDataset(datasetId);
    setDataset(data.results);
  }

  useEffect(() => {
    if (!datasetId || metadata != null) return;
    fetchData(datasetId);
  }, [datasetId]);

  const { rowData, colDefs } = useMemo(() => {
    if (!dataset || dataset.length === 0) {
      return { rowData: [], colDefs: [] as ColDef[] };
    }

    const allKeys = new Set<string>();
    dataset.forEach((result) => {
      Object.keys(result).forEach((key) => allKeys.add(key));
    });

    const columns: ColDef[] = Array.from(allKeys).map((key) => ({
      field: key,
      headerName: key,
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params: any) => {
        if (
          params.value === null ||
          params.value === undefined ||
          params.value === ""
        ) {
          return "—";
        }
        if (typeof params.value === "object") {
          return JSON.stringify(params.value);
        }
        return String(params.value);
      },
      cellStyle: (params: any) => {
        if (
          params.value === null ||
          params.value === undefined ||
          params.value === ""
        ) {
          return { color: "var(--mantine-color-gray-5)" };
        }
        return null;
      },
    }));

    return { rowData: dataset, colDefs: columns };
  }, [dataset]);

  const datasetFlagSrc = useMemo(() => {
    if (!metadata) return undefined;
    const language = languages.find(
      (lang: Language) => lang.iso2 === metadata.language_iso
    );
    return language?.flag_file;
  }, [languages, metadata?.language_iso]);

  const datasetSize = useMemo(
    () => formatBytes(metadata?.byte_count ?? 0),
    [metadata?.byte_count]
  );

  if (loading) return <Text>Loading...</Text>;

  if (!metadata || !dataset) {
    return <Text>No dataset found...</Text>;
  }

  const datasetName =
    metadata.table_name_translation?.[currentLanguage] ??
    metadata.table_name_original ??
    "Unknown dataset";
  const datasetDescription =
    metadata.description_long_translation?.[currentLanguage] ??
    metadata.description_long_original ??
    "No description available";

  const datasetSourceName =
    metadata.db_table_organization?.organization?.organization_name ??
    "Unknown source";

  const datasetSourceUrl =
    metadata.db_table_organization?.organization?.website;

  const datasetRowCount = (metadata.row_count ?? 0).toLocaleString("da-DK", {
    style: "decimal",
    maximumFractionDigits: 0,
  });
  const datasetColumnCount = (metadata.column_count ?? 0).toLocaleString(
    "da-DK",
    {
      style: "decimal",
      maximumFractionDigits: 0,
    }
  );
  const datasetDateCreated = metadata.datetime_created
    ? new Date(metadata.datetime_created).toLocaleDateString("da-DK", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "Unknown date";

  return (
    <Flex direction="column" gap="md" align="center">
      <Flex
        direction="column"
        gap="md"
        p={80}
        w="100%"
        maw={1200}
        align="flex-start"
      >
        <Button
          leftSection={<IconArrowLeft stroke={1.5} size={16} />}
          variant="light"
          bdrs="xl"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Title>{datasetName}</Title>
        <Group gap={8} align="center" my={4}>
          <Image
            src={datasetFlagSrc}
            width={14}
            height={10}
            style={{
              objectFit: "cover",
              width: 14,
              height: 10,
              borderRadius: "1px",
              filter: "drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.15))",
            }}
          />
          {datasetSourceUrl ? (
            <a
              className="dataset-card-source"
              href={
                datasetSourceUrl.startsWith("http")
                  ? datasetSourceUrl
                  : `https://${datasetSourceUrl}`
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <Text fz={15} c="gray.9" fw={400}>
                {datasetSourceName}
              </Text>
            </a>
          ) : (
            <Text fz={15} c="gray.9" fw={400}>
              {datasetSourceName}
            </Text>
          )}
        </Group>
        <Text lh={1.8}>{datasetDescription}</Text>
        <Flex direction="row" gap="md">
          <Text fz={15} c="gray.9" fw={400}>
            {datasetRowCount} rows · {datasetColumnCount} cols
          </Text>
          <Divider orientation="vertical" color="gray.3" h={16} my="auto" />
          <Text fz={15} c="gray.9" fw={400}>
            {datasetSize}
          </Text>
          <Divider orientation="vertical" color="gray.3" h={16} my="auto" />
          <Text fz={15} c="gray.9" fw={400}>
            {datasetDateCreated}
          </Text>
        </Flex>
      </Flex>
      {dataset.length > 0 && (
        <div style={{ width: "100%" }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={{
              resizable: true,
            }}
            domLayout="autoHeight"
            onGridReady={(params) => {
              params.api.autoSizeAllColumns();
            }}
          />
        </div>
      )}
    </Flex>
  );
}
